/**
 * 選択カード（I52）。≥2 bp で長さ・GC%。≥18 bp で F/R コピー。
 */

import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  designPrimerPair,
  formatIntervalRange1Based,
  type ChunkedSequence,
  type Interval,
} from '@kensuke/genome-core';

export interface GeneAnalysisSelectionCardProps {
  sequence: ChunkedSequence;
  selection: Interval;
  hidePrimers?: boolean;
}

export default function GeneAnalysisSelectionCard({
  sequence,
  selection,
  hidePrimers = false,
}: GeneAnalysisSelectionCardProps) {
  const { t } = useTranslation();
  const length = selection.end - selection.start;
  const gc = sequence.gcPercentSync(selection);
  const [copied, setCopied] = useState<string | null>(null);
  const [pair, setPair] = useState<ReturnType<typeof designPrimerPair> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (hidePrimers || length < 18) {
      setPair(null);
      return;
    }
    void sequence.read(selection).then((codes) => {
      if (!cancelled) setPair(designPrimerPair(codes));
    });
    return () => {
      cancelled = true;
    };
  }, [hidePrimers, length, selection, sequence]);

  if (length < 2) return null;

  const copy = async (label: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1600);
  };

  return (
    <div className="ga-selection-card" data-testid="gene-analysis-selection-card">
      <strong>{t('geneAnalysis.selection.title')}</strong>
      <span>
        {formatIntervalRange1Based(selection, sequence.length)} · {length.toLocaleString()} bp
        {gc === null ? '' : ` · GC ${gc.toFixed(1)}%`}
      </span>
      {!hidePrimers && pair?.forward && (
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => void copy('F', pair.forward?.sequence ?? '')}
        >
          {copied === 'F' ? t('geneAnalysis.selection.copied') : `F ${pair.forward.tm.toFixed(1)}°C`}
        </Button>
      )}
      {!hidePrimers && pair?.reverse && (
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => void copy('R', pair.reverse?.sequence ?? '')}
        >
          {copied === 'R' ? t('geneAnalysis.selection.copied') : `R ${pair.reverse.tm.toFixed(1)}°C`}
        </Button>
      )}
    </div>
  );
}
